import {
  CircularProgress,
} from "@mui/material";
import CustomDataGrid from "../../../../components/Shared/Table/CustomDataGrid";
import { topOffenderColumns } from "../../../../components/Shared/Table/Columns";
import { IServiceDeviceData } from "../../../../store/api/devices/types";
import { useTopOffenderDisplay } from "../hooks";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import DeviceDrawer from "../../../../components/Drawers/DeviceDrawer";
import { useStoreDispatch } from "../../../../store/hooks";
import { useSelector } from "react-redux";
import {
  useGetAlarmsByDeviceKeyMutation,
  useGetDrawerCountsMutation,
  useExportTopOffendersMutation,
} from "../../../../store/api/devices/deviceApiSlice";
import {
  useGetDeviceByKeyQuery,
  useGetDeviceFullDetailMutation,
} from "../../../../store/api/deviceConfig/deviceConfigApiSlice";
import {
  setDrawerCounts,
  setOpenDeviceDrawer,
  setSelectedDevice,
  setSelectedDeviceAlarmCount,
  setSelectedDeviceAlarms,
} from "../../../../store/api/devices/reducers";
import {
  setIsLoadingStatus,
  setOpenToast,
  setPaginationModel,
} from "../../../../store/helpers/helperReducers";
import { selectProfilePermissionsSelector } from "../../../../store/api/auth/selectors";
import ProfilePermissions from "../../../../utils/ProfilePermissions";
import { errorMessage, exportInCSV } from "../../../../utils/helpers";
import TopOffenderFilter from "./TopOffenderFilter";
import { topOffenderFilterSelector } from "../../../../store/filters/topoffender/selector";
import { selectedGeoRegionsSelector } from "../../../../store/api/admin/selectors";
import { IGeoRegion } from "../../../../store/api/admin/types";
import { useAlarmDataGrid } from "./hooks";
import { paginationModelSelector } from "../../../../store/helpers/helperSelectors";
import { Button } from "../../../../components/ui/button";
import { Icon } from "../../../../components/Shared/Icon";
import { Loader2 } from "lucide-react";

interface ToolbarProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  exportHandler: () => void;
  isExporting: boolean;
}

const DataGridToolbar: React.FC<ToolbarProps> = ({
  searchText,
  setSearchText,
  exportHandler,
  isExporting,
}) => {
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <div className="relative w-80">
          <SearchIcon
            sx={{ fontSize: 18 }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search by Serial Number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-md
                       focus:outline-none focus:ring-1 focus:ring-ring
                       focus:border-ring transition duration-200"
            style={{
              backgroundColor: "var(--card)",
              color: "var(--foreground)",
              fontSize: "0.875rem",
              height: 36,
            }}
          />
        </div>
        <TopOffenderFilter onReset={() => setSearchText("")} externalSearchActive={searchText.trim().length > 0} />
      </div>
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        disabled={isExporting}
        onClick={exportHandler}
        aria-label="Export CSV"
      >
        {isExporting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Icon name="download" size={18} />
        )}
      </Button>
    </div>
  );
};

interface TopOffenderProps {
  selectedGeoRegions: string[];
}

const TopOffender: React.FC<TopOffenderProps> = ({
  selectedGeoRegions,
}) => {

  /* ------------------------- STATE FIRST ------------------------- */

  const pagination = useSelector(paginationModelSelector);
  const currentPagination =
    pagination["topOffenderNodes"] || { page: 0, pageSize: 10 };

  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [selectedRowData, setSelectedRowData] =
    useState<IServiceDeviceData | undefined>();
  const [rowClicked, setRowClicked] = useState(false);

  /* ------------------------- REDUX ------------------------- */

  const dispatch = useStoreDispatch();
  const filterState = useSelector(topOffenderFilterSelector);
  const selectedGeoRegionsFull = useSelector(selectedGeoRegionsSelector) as IGeoRegion[];
  const selectedGeoRegionNames = selectedGeoRegionsFull?.map((r: IGeoRegion) => r.name) || [];
  const permissions: ProfilePermissions | null =
    useSelector(selectProfilePermissionsSelector);

  /* ------------------------- CUSTOM HOOKS ------------------------- */


  const { rows, totalElements, isLoading } =
    useTopOffenderDisplay(
      currentPagination.page,
      currentPagination.pageSize,
      debouncedSearchText
    );


  /* ------------------------- API MUTATIONS ------------------------- */

  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] =
    useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] =
    useGetAlarmsByDeviceKeyMutation();
  const [exportTopOffenders] = useExportTopOffendersMutation();

  /* ------------------------- HANDLERS ------------------------- */

  const searchArgs = {
    filters: searchText
      ? `key=objectKey&value=${searchText}&weightType=FULL`
      : "",
    deviceKey: searchText,
  };

  const { data: searchResult, isLoading: isSearchLoading } =
    useGetDeviceByKeyQuery(searchArgs, {
      skip: !searchText,
    });

  const searchRows = searchText
    ? searchResult?.data || []
    : [];

  const finalRows = searchText ? searchRows : rows;
  const finalRowCount = searchText
    ? searchRows.length
    : totalElements;



  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleRowClicked = async (params: any) => {
    if (
      params.row.modelName === "migration" ||
      params.row.modelName === "unknown"
    )
      return;

    dispatch(
      setIsLoadingStatus({
        isLoadingDeviceDetail: true,
        isLoadingAlarms: true,
        isLoadingCounts: true,
      })
    );

    setRowClicked(true);
    dispatch(setOpenDeviceDrawer(true));
    setSelectedRowData(params.row);

    try {
      const args = {
        filters: `key=objectKey&value=${params.row.objectKey}&weightType=FULL`,
        deviceKey: params.row.objectKey,
      };

      const deviceDataResponse: any =
        await getDeviceFullDetail(args);

      if ("error" in deviceDataResponse)
        throw deviceDataResponse.error;

      if (deviceDataResponse?.data?.data?.[0]) {
        dispatch(
          setSelectedDevice({
            ...deviceDataResponse.data.data[0],
            ...deviceDataResponse.data?.deviceParams,
          })
        );
        dispatch(
          setIsLoadingStatus({
            isLoadingDeviceDetail: false,
          })
        );
      }

      const configCountResponse: any =
        await getDrawerCounts({
          deviceKey: params.row.objectKey,
          filters: "",
        });

      if ("error" in configCountResponse)
        throw configCountResponse.error;

      dispatch(setDrawerCounts(configCountResponse.data));
      dispatch(
        setIsLoadingStatus({
          isLoadingCounts: false,
        })
      );

      if (permissions?.hasOpCodeGroup("view_events")) {
        const alarmDataResponse: any =
          await getAlarmsByDeviceKey({
            deviceKey: params.row.objectKey,
            filters:
              "?sort=retention%2Cdesc&page=0&size=50",
          });

        if ("error" in alarmDataResponse)
          throw alarmDataResponse.error;

        dispatch(
          setSelectedDeviceAlarms(
            alarmDataResponse.data?.data
          )
        );
        dispatch(
          setSelectedDeviceAlarmCount(
            alarmDataResponse.data?.totalItems || 0
          )
        );
        dispatch(
          setIsLoadingStatus({
            isLoadingAlarms: false,
          })
        );
      }
    } catch (error: any) {
      dispatch(
        setIsLoadingStatus({
          isLoadingDeviceDetail: false,
          isLoadingAlarms: false,
        })
      );
      dispatch(setOpenDeviceDrawer(false));
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    }
  };

  const exportHandler = async () => {
    try {
      setIsExporting(true);

      const params = new URLSearchParams();

      if (filterState.selectedRegion) {
        params.append("geoRegions", filterState.selectedRegion);
      } else if (selectedGeoRegionNames.length > 0) {
        params.append("geoRegions", selectedGeoRegionNames.join(","));
      }

      if (filterState.selectedType)
        params.append("type", filterState.selectedType);

      if (filterState.selectedStatus)
        params.append("status", filterState.selectedStatus);

      if (filterState.selectedConfigStatus)
        params.append("configStatus", filterState.selectedConfigStatus);

      if (debouncedSearchText && debouncedSearchText.trim())
        params.append("search", debouncedSearchText.trim());

      if (filterState.selectedAlarmType?.length) {
        filterState.selectedAlarmType.forEach((alarm: string) => {
          params.append("alarmType", alarm);
        });
      }

      const response: any = await exportTopOffenders(params.toString());
      if (response?.error) throw response?.error;
      exportInCSV(response, "Top_Offenders");
    } catch (error: any) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: errorMessage(error),
        })
      );
    } finally {
      setIsExporting(false);
    }
  };

  // Debounce search text - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchText]);

  useEffect(() => {
    dispatch(
      setPaginationModel({
        topOffenderNodes: {
          page: 0,
          pageSize: currentPagination.pageSize,
        },
      })
    );
  }, [filterState, debouncedSearchText]);

  /* ------------------------- RENDER ------------------------- */

  return (
    <>
      {rowClicked && selectedRowData && (
        <DeviceDrawer
          data={selectedRowData}
          closedButtonClicked={handleCloseButtonClicked}
        />
      )}

      <div className="w-full space-y-4 my-3">
        <DataGridToolbar
          searchText={searchText}
          setSearchText={setSearchText}
          exportHandler={exportHandler}
          isExporting={isExporting}
        />

        <div style={{ width: "100%", height: 600 }}>
          <CustomDataGrid
            tableId="topOffenderNodes"
            rows={rows}
            columns={topOffenderColumns}
            rowCount={totalElements}
            loading={isLoading}
            disableColumnMenu
            getRowId={(row: IServiceDeviceData) =>
              row.objectKey
            }
            paginationMode="server"
            // sx={dataGridSx}
            onRowClick={handleRowClicked}
          />
        </div>
      </div>
    </>
  );
};


export default TopOffender;
